import React, { useCallback, useEffect, useState } from "react";
import {
  Typography,
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Info as InfoIcon,
  Chat as ChatIcon,
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import { agentApi } from "../services/api";
import { Agent } from "../types";
import { useLoading } from "../context/LoadingContext";
import { useNotification } from "../context/NotificationContext";
import { useErrorHandler } from "../hooks/useErrorHandler";

const Agents = () => {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [agentToDelete, setAgentToDelete] = useState<Agent | null>(null);
  const { startLoading, stopLoading } = useLoading();
  const { showNotification } = useNotification();
  const { handleError } = useErrorHandler();

  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const fetchAgents = useCallback(async () => {
    // Prevent multiple simultaneous requests
    if (isLoading) return;
    
    try {
      setIsLoading(true);
      startLoading();
      const data = await agentApi.getAgents();
      setAgents(data);
      setHasError(false);
    } catch (error) {
      handleError(error);
      setHasError(true); // Mark that we had an error
      console.error('Failed to fetch agents:', error);
    } finally {
      setIsLoading(false);
      stopLoading();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleError]); // Only depend on handleError to avoid infinite loops

  useEffect(() => {
    // Only fetch if we haven't had an error yet
    if (!hasError) {
      fetchAgents();
    }
  }, [fetchAgents, hasError]);
  
  // Add a retry button for when we have errors
  const handleRetry = () => {
    setHasError(false); // Reset error state
    fetchAgents();
  };

  const handleOpenDeleteDialog = (agent: Agent) => {
    setAgentToDelete(agent);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setAgentToDelete(null);
  };

  const handleDeleteAgent = async () => {
    if (!agentToDelete) return;

    try {
      startLoading();
      await agentApi.deleteAgent(agentToDelete.id);
      showNotification(
        `Agent "${agentToDelete.name}" deleted successfully`,
        "success"
      );
      fetchAgents(); // Refresh the list
    } catch (error) {
      handleError(error);
    } finally {
      stopLoading();
      handleCloseDeleteDialog();
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Typography variant="h4" component="h1" gutterBottom>
          Agents
        </Typography>
        <Button
          variant="contained"
          color="primary"
          component={Link}
          to="/agents/create"
        >
          Create New Agent
        </Button>
      </Box>

      {hasError ? (
        <Box sx={{ textAlign: 'center', my: 4 }}>
          <Typography variant="body1" color="error" gutterBottom>
            Failed to load agents. There might be a connection issue with the backend server.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleRetry}
            sx={{ mt: 2 }}
          >
            Retry
          </Button>
        </Box>
      ) : agents.length === 0 ? (
        <Typography variant="body1" color="text.secondary">
          No agents found. Create your first agent to get started.
        </Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Model</TableCell>
                <TableCell>Created At</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {agents.map((agent) => (
                <TableRow key={agent.id}>
                  <TableCell component="th" scope="row">
                    {agent.name}
                  </TableCell>
                  <TableCell>{agent.model}</TableCell>
                  <TableCell>
                    {new Date(agent.createdAt).toLocaleString()}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      component={Link}
                      to={`/chat/${agent.id}`}
                      color="primary"
                      aria-label="chat with agent"
                    >
                      <ChatIcon />
                    </IconButton>
                    <IconButton
                      component={Link}
                      to={`/agents/${agent.id}`}
                      color="primary"
                      aria-label="agent details"
                    >
                      <InfoIcon />
                    </IconButton>
                    <IconButton
                      color="error"
                      aria-label="delete agent"
                      onClick={() => handleOpenDeleteDialog(agent)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Delete Agent</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the agent "{agentToDelete?.name}"?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={handleDeleteAgent} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Agents;
